import { useMemo } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { type IconType } from '@verantos-dev/shared-components';

import type { SidebarConfig, SidebarContext } from '../layout/AppPageLayout/types';
import {
  AnnotationToolFeatureId,
  AnnotationToolSidebarAppID,
  getAnnotationToolBasePath,
  getAnnotationToolDashboardPath,
  getAnnotationToolSupervisorPath,
  getAnnotationToolAnnotatorQueuePath,
  getAnnotationToolAdminProjectsPath,
  getAnnotationToolAnnotatorProjectPath,
  getAnnotationToolSupervisorProjectPath,
  getAnnotationToolMetricsPath,
  getAnnotationToolMetricsDashboardPath,
  getAnnotationToolMetricsSummaryPath,
  getAnnotationToolMetricsEncounterStatsPath,
  getAnnotationToolMetricsIRRScorePath,
  getAnnotationToolMetricsHolidaysPath,
} from '../lib';

/**
 * Internal Platform-specific sidebar configuration hook.
 * Provides all the Internal Platform-specific mappings for icons, URLs, handlers, and patterns.
 */
export const useAnnotationToolSidebarConfig = (): SidebarConfig => {
  return useMemo(() => {
    const getIcon = (appId: string): 'noIcon' | ('blank' | IconType) => {
      switch (appId) {
        case AnnotationToolSidebarAppID.annotationTool:
          return 'sidebarDataAdmin';
        case AnnotationToolFeatureId.annotationDashboard:
          return 'folder';
        case AnnotationToolFeatureId.supervisorProjects:
        case AnnotationToolFeatureId.annotatorProjects:
        case AnnotationToolFeatureId.adminProjects:
          return 'createSubgroup';
        case AnnotationToolFeatureId.annotationSupervisor:
          return 'sidebarDataAdmin';
        case AnnotationToolFeatureId.annotation:
          return 'showData';
        case 'administration-workspace':
          return 'viewSubjects';
        case AnnotationToolFeatureId.metrics:
          return 'viewResults';
        case AnnotationToolFeatureId.metricsDashboard:
          return 'studyArm';
        case AnnotationToolFeatureId.metricsSummary:
          return 'sidebarStudyDetails';
        case AnnotationToolFeatureId.metricsEncounterStats:
          return 'insert_chart';
        case AnnotationToolFeatureId.metricsIRRScore:
          return 'subgroup';
        case AnnotationToolFeatureId.metricsHolidays:
          return 'showData';
        default:
          return 'noIcon';
      }
    };

    const getUrl = (appId: string): string => {
      switch (appId) {
        case AnnotationToolSidebarAppID.annotationTool:
          return getAnnotationToolBasePath();
        case AnnotationToolFeatureId.annotationDashboard:
          return getAnnotationToolDashboardPath();
        case AnnotationToolFeatureId.supervisorProjects:
          return getAnnotationToolSupervisorProjectPath();
        case AnnotationToolFeatureId.annotationSupervisor:
          return getAnnotationToolSupervisorPath();
        case AnnotationToolFeatureId.adminProjects:
          return getAnnotationToolAdminProjectsPath();
        case AnnotationToolFeatureId.annotatorProjects:
          return getAnnotationToolAnnotatorProjectPath();
        case AnnotationToolFeatureId.annotation:
          return getAnnotationToolAnnotatorQueuePath();
        case AnnotationToolFeatureId.metrics:
          return getAnnotationToolMetricsPath();
        case AnnotationToolFeatureId.metricsDashboard:
          return getAnnotationToolMetricsDashboardPath();
        case AnnotationToolFeatureId.metricsSummary:
          return getAnnotationToolMetricsSummaryPath();
        case AnnotationToolFeatureId.metricsEncounterStats:
          return getAnnotationToolMetricsEncounterStatsPath();
        case AnnotationToolFeatureId.metricsIRRScore:
          return getAnnotationToolMetricsIRRScorePath();
        case AnnotationToolFeatureId.metricsHolidays:
          return getAnnotationToolMetricsHolidaysPath();
        case 'administration-workspace':
          return getAnnotationToolDashboardPath(); // Administration Workspace goes to dashboard
        default:
          return `/${appId}`;
      }
    };

    const getClickHandler = (
      appId: string,
      navigateFn: NavigateFunction,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _context: SidebarContext
    ) => {
      return () => {
        navigateFn(getUrl(appId));
      };
    };

    const getMatchPatterns = (appId: string): string[] => {
      switch (appId) {
        case AnnotationToolSidebarAppID.annotationTool:
          return [AnnotationToolSidebarAppID.annotationTool];
        case AnnotationToolFeatureId.annotationDashboard:
          return [AnnotationToolFeatureId.annotationDashboard, 'annotation-dashboard'];
        case AnnotationToolFeatureId.annotationSupervisor:
          return [AnnotationToolFeatureId.annotationSupervisor, 'annotation-supervisor'];
        case AnnotationToolFeatureId.annotation:
          return [AnnotationToolFeatureId.annotation, 'annotator-queue'];
        case AnnotationToolFeatureId.adminProjects:
          return [AnnotationToolFeatureId.adminProjects, 'admin-projects'];
        case AnnotationToolFeatureId.metricsDashboard:
          return [AnnotationToolFeatureId.metricsDashboard, 'dashboard'];
        case AnnotationToolFeatureId.metricsSummary:
          return [AnnotationToolFeatureId.metricsSummary, 'summary'];
        case AnnotationToolFeatureId.metricsEncounterStats:
          return [AnnotationToolFeatureId.metricsEncounterStats, 'encounterStats'];
        case AnnotationToolFeatureId.metricsIRRScore:
          return [AnnotationToolFeatureId.metricsIRRScore, 'irrScore'];
        case AnnotationToolFeatureId.metricsHolidays:
          return [AnnotationToolFeatureId.metricsHolidays, 'holidays'];
        case 'administration-workspace':
          return [
            'administration-workspace',
            AnnotationToolFeatureId.annotationDashboard,
            'annotation-dashboard',
          ];
        default:
          return [appId];
      }
    };

    const getContext = () => ({});

    return {
      getIcon,
      getUrl,
      getClickHandler,
      getMatchPatterns,
      getContext,
      dividerConfig: {
        // Configure divider behavior as needed for Internal Platform
        noDividerForActiveSubItems: [],
        noDividerForSingleItems: [],
      },
      // No additional items for Internal Platform by default
      additionalItems: [],
    };
  }, []);
};
